import { getAccessToken } from "../utils/auth";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../utils/api";
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Grid,
  Skeleton,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { FiArrowRight } from "react-icons/fi";

const TEAL = "#0d9488";

const styles = {
  pageWrap: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
  },
  headerSection: {
    padding: "2rem 1.5rem 1.5rem",
    background:
      "linear-gradient(180deg, rgba(15, 23, 42, 0.03) 0%, transparent 100%)",
    borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
  },
  headerContent: {
    maxWidth: 1280,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  pageTitle: {
    fontSize: "clamp(1.75rem, 4vw, 2.25rem)",
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.02em",
    margin: 0,
    lineHeight: 1.2,
  },
  pageSubtitle: {
    fontSize: "clamp(0.9rem, 2vw, 1rem)",
    color: "#64748b",
    marginTop: "0.35rem",
    fontWeight: 400,
  },
  mainContent: {
    maxWidth: 1280,
    margin: "0 auto",
    padding: "1.5rem",
    paddingBottom: "3rem",
  },
  cardContainer: {
    justifyContent: "flex-start",
  },
  card: {
    height: "100%",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    backgroundColor: "#ffffff",
  },
  cardContent: {
    padding: "1.5rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    height: "100%",
    boxSizing: "border-box",
    position: "relative",
    zIndex: 1,
  },
  iconWrap: {
    width: 52,
    height: 52,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "1rem",
    color: TEAL,
    backgroundColor: `${TEAL}14`,
  },
  cardTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#0f172a",
    margin: 0,
    marginBottom: "0.35rem",
    lineHeight: 1.3,
    letterSpacing: "-0.01em",
  },
  cardDescription: {
    fontSize: "0.875rem",
    color: "#64748b",
    lineHeight: 1.45,
    marginBottom: "1rem",
    flex: 1,
  },
  cardArrow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "#475569",
    opacity: 0.85,
  },
};

function RobotCard({ robot, onClick, animationDelay = 0, historyCount = null }) {
  const [imgError, setImgError] = useState(false);
  const locationText = [robot.location, robot.subLocation].filter(Boolean).join(" · ") || "—";
  const showImage = robot.image && !imgError;
  const title = robot.username || "Unnamed robot";
  const description = [robot.robotId, locationText].filter(Boolean).join(" · ") || "View history";

  const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  `;

  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      lg={3}
      sx={{
        animation: `${fadeInUp} 0.4s ease-out ${animationDelay}s forwards`,
        opacity: 0,
      }}
    >
      <Card
        style={styles.card}
        onClick={onClick}
        sx={{
          cursor: onClick ? "pointer" : "default",
          position: "relative",
          "&:before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(to right, #0048e6, #9C15F7, rgba(255, 75, 248, 0.3))",
            opacity: 0,
            zIndex: 0,
            transition: "opacity 0.25s ease",
          },
          "&:hover": onClick
            ? {
                "&:before": {
                  opacity: 0.28,
                },
              }
            : {},
        }}
      >
        <CardContent style={styles.cardContent}>
          <Box style={styles.iconWrap} sx={{ overflow: "hidden" }}>
            {showImage ? (
              <Box
                component="img"
                src={robot.image}
                alt=""
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={() => setImgError(true)}
              />
            ) : (
              <SmartToyOutlinedIcon sx={{ fontSize: 28 }} />
            )}
          </Box>
          <Typography component="h3" style={styles.cardTitle}>
            {title}
          </Typography>
          <Typography style={styles.cardDescription}>{description}</Typography>
          {historyCount != null && (
            <Typography variant="caption" sx={{ color: "#64748b", mb: 1 }}>
              {historyCount} history {historyCount === 1 ? "record" : "records"}
            </Typography>
          )}
          <Box style={styles.cardArrow}>
            <span>Open</span>
            <FiArrowRight size={16} style={{ flexShrink: 0 }} />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}

function SkeletonCard() {
  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card style={styles.card}>
        <CardContent style={styles.cardContent}>
          <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: 14, mb: 1.5 }} />
          <Skeleton variant="text" width="70%" height={24} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={48} height={18} />
        </CardContent>
      </Card>
    </Grid>
  );
}

export default function AnalysisRobotsListScreen() {
  const [robots, setRobots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyCountByRobotId, setHistoryCountByRobotId] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        const res = await axios.get("/robots-get", {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        setRobots(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching robots:", err);
        setError(err.response?.data?.message || err.message || "Failed to load robots.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (robots.length === 0) return;
    const token = getAccessToken();
    const fetchCounts = async () => {
      const results = await Promise.all(
        robots.map(async (robot) => {
          const id = robot.robotId;
          if (!id) return [id, null];
          try {
            const res = await axios.get("/robot/history/count", {
              params: { robotId: id },
              headers: { Authorization: `Bearer ${token}` },
            });
            const data = res.data;
            const count =
              data?.totalHistory ?? data?.total ?? data?.count ?? (typeof data === "number" ? data : null);
            return [id, typeof count === "number" ? count : null];
          } catch {
            return [id, null];
          }
        })
      );
      setHistoryCountByRobotId(Object.fromEntries(results));
    };
    fetchCounts();
  }, [robots]);

  return (
    <Box style={styles.pageWrap}>
      <Box style={styles.headerSection}>
        <Box style={styles.headerContent}>
          <IconButton
            onClick={() => navigate("/disinfection-analysis")}
            size="small"
            sx={{
              bgcolor: "white",
              border: "1px solid rgba(15, 23, 42, 0.08)",
              "&:hover": { bgcolor: "grey.50" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="h1" style={styles.pageTitle}>
              Robots-History
            </Typography>
            <Typography style={styles.pageSubtitle}>
              {loading ? "Loading…" : error ? "—" : `${robots.length} robots registered`}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box style={styles.mainContent}>
        {loading && (
          <Grid container spacing={3} style={styles.cardContainer}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </Grid>
        )}

        {error && !loading && (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography variant="body2" color="error.main">
              {error}
            </Typography>
          </Box>
        )}

        {!loading && !error && robots.length === 0 && (
          <Box sx={{ py: 8, textAlign: "center", color: "text.secondary" }}>
            <SmartToyOutlinedIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">No robots found.</Typography>
          </Box>
        )}

        {!loading && !error && robots.length > 0 && (
          <Grid container spacing={3} style={styles.cardContainer}>
            {robots.map((robot, index) => (
              <RobotCard
                key={robot.robotId || index}
                robot={robot}
                onClick={() => robot.robotId && navigate(`/analytics/robot/${robot.robotId}/history`)}
                animationDelay={index * 0.05}
                historyCount={robot.robotId ? historyCountByRobotId[robot.robotId] ?? null : null}
              />
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
